import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { KmAttrValueField } from './attrs/KmAttrValueField';
import type { KmAttrTypeResolver } from './attrs/attrFormValue';
import type { KmExtraAttrRow, KmSchemaAttrRow } from './attrs/useKmEntityAttrsForm';
import { formatKmAttrValue } from './formatKmAttrValue';
import type { KmEntityCardFormValues } from './useKmEntityCardForm';

/** Раскладка строки атрибута: подпись с кодом и ввод значения рядом, на узкой панели — друг под другом */
const ATTR_ROW_CLASS = 'grid grid-cols-1 gap-1 sm:grid-cols-[240px_1fr] sm:gap-4';

type Props = {
  /** Строки по схеме типа экземпляра: их значения правятся формой карточки */
  schemaRows: KmSchemaAttrRow[];
  /** Значения с кодами вне схемы типа: только показываются */
  extraRows: KmExtraAttrRow[];
  resolveAttrType: KmAttrTypeResolver;
  /** Доступно ли изменение значений (право на администрирование модели знаний) */
  canEdit: boolean;
};

/**
 * Секция «Атрибуты»: значения экземпляра по схеме его типа. Каждое значение вводится
 * по типу своего атрибута, значения с кодами вне схемы показываются, но не изменяются.
 * Своей кнопки сохранения у секции нет — значения сохраняются кнопкой карточки.
 */
export const KmEntityAttrsSection: FC<Props> = ({ schemaRows, extraRows, resolveAttrType, canEdit }) => {
  const { t } = useTranslation(['kmEntities', 'common']);
  const { control } = useFormContext<KmEntityCardFormValues>();

  const booleanLabels = useMemo(() => ({ yes: t('common:yes'), no: t('common:no') }), [t]);

  if (!schemaRows.length && !extraRows.length) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="items-center border-b">
        <CardTitle className="text-base">{t('kmEntities:attrsSectionTitle')}</CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-4 p-2 md:p-6">
        {schemaRows.map((row, index) => (
          <div key={row.code} className={ATTR_ROW_CLASS}>
            <div className="flex flex-col">
              <span className="text-sm">{row.isRequired ? `${row.name} *` : row.name}</span>
              <span className="font-mono text-xs text-muted-foreground">{row.code}</span>
              {!row.isFilled && <span className="text-xs text-muted-foreground">{t('kmEntities:attrNotFilled')}</span>}
            </div>

            {row.attrType ? (
              <KmAttrValueField
                control={control}
                name={`rows.${index}.value`}
                attrType={row.attrType}
                resolveAttrType={resolveAttrType}
                disabled={!canEdit}
                placeholder={t('kmEntities:attrNotFilled')}
              />
            ) : (
              // Атрибута нет в справочнике — способ ввода выбирать не по чему, остаётся показ
              <span className="text-sm break-words">{formatKmAttrValue(row.value, booleanLabels)}</span>
            )}
          </div>
        ))}

        {extraRows.map((row) => (
          <div key={row.code} className={ATTR_ROW_CLASS}>
            <div className="flex flex-col">
              {/* Значение вне схемы типа названия не имеет — на него указывает только код */}
              <span className="text-sm text-muted-foreground">{t('kmEntities:attrOutOfSchema')}</span>
              <span className="font-mono text-xs text-muted-foreground">{row.code}</span>
            </div>
            <span className="text-sm break-words">{formatKmAttrValue(row.value, booleanLabels)}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
